import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import DefaultLayout from '@/components/layout/DefaultLayout';
import { CommonPageProps, ImageShape, NonUndefined, Product } from '@/types';
import { getCommonPageProps } from '@/api/common';
import { tp } from '@/typograf';
import {
    ArticleConstuctorBlocks,
    ArticleImage,
    ArticleProducts,
    ArticleVideo,
    ArticleWysiwyg,
    getArticle,
} from '@/api/article';
import ArticleHero from '@/components/layout/pages/article/ArticleHero';
import ArticleBody from '@/components/layout/pages/article/ArticleBody';
import { ApiArticleTag } from '@/api/tags';
import { getProducts } from '@/api/products';

const DetailPage = ({
    h1,
    heroImage,
    signature,
    tags,
    published_at,
    constructor,
    products,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    return (
        <DefaultLayout>
            <h1 className="visually-hidden">{h1}</h1>
            <ArticleHero name={h1} image={heroImage} />
            <ArticleBody
                title={h1}
                signature={signature}
                tags={tags}
                published_at={published_at}
                constructor={constructor}
                products={products}
            />
        </DefaultLayout>
    );
};

export default DetailPage;

type ProductPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        heroImage: ImageShape;
        signature: string;
        tags: ApiArticleTag[];
        published_at: number;
        constructor: ArticleConstuctorBlocks;
        products: Product[];
    }
>;

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({ query }) => {
    const articleSlug = query.slug as string;

    if (!articleSlug) {
        return { notFound: true };
    }

    const articleInfo = await getArticle(articleSlug)
        .then((res) => res)
        .catch((err) => err);

    if (articleInfo.response && articleInfo.response.data.status === 'error') {
        if (articleInfo.response.status === 404) {
            return { notFound: true };
        }
        throw articleInfo.repsonse.data.errors[0];
    }

    const productsBlocks = articleInfo.content.filter(
        (item: ArticleWysiwyg | ArticleImage | ArticleVideo | ArticleProducts) => item.name === 'products',
    );

    const ids = productsBlocks.reduce((acc: number[], item: ArticleProducts) => {
        if (item.properties.products) {
            acc.push(...item.properties.products.map((item) => item.id));
        }
        return acc;
    }, []);

    const [commonPageProps /* , products */] = await Promise.all([
        getCommonPageProps() /* , getProducts({ params: { ids } }) */,
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'article-page',
            meta: {
                ...commonPageProps.meta,
                ...articleInfo?.meta,
            },
            h1: tp(articleInfo?.name || ''),
            heroImage: articleInfo?.detail_image ?? {
                src: '',
                width: 0,
                height: 0,
                alt: '',
            },
            signature: articleInfo?.signature ?? '',
            tags: articleInfo?.tags ?? [],
            published_at: articleInfo?.published_at ?? 0,
            constructor: articleInfo?.content ?? [],
            products: /*  products?.data?.items ?? */ [],
        } satisfies ProductPageProps,
    };
};
